/**
 * Fleet Performance Report Output View
 *
 * Main component that fetches fleet performance data and renders
 * Top 5 (Best) and Worst 5 vehicle tables. Includes error boundary
 * for unexpected rendering errors, and handles loading, error, and
 * empty data states.
 *
 * Requirements: 2.1, 2.2, 2.3, 2.8, 2.9
 */

import React from "react";
import { FleetPerformanceTable } from "./FleetPerformanceTable";
import { getFleetPerformanceData } from "./services";

/**
 * Error Boundary class component to catch unexpected rendering errors
 * in the FleetPerformanceReport tree and display a fallback UI.
 */
class FleetPerformanceErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("FleetPerformanceReport error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="fleet-performance-report_error">
          <p>Report could not be generated</p>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * FleetPerformanceReport Component
 *
 * Fetches fleet performance data on mount and renders two ranked tables:
 * - Top 5 vehicles ("Best Of 5 Vehicle")
 * - Worst 5 vehicles ("Worst of 5 Vehicle")
 *
 * @param {Object} props
 * @param {Object} [props.params] - Optional parameters passed to the service
 */
export const FleetPerformanceReport = ({ params = {} }) => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    let isMounted = true;

    setLoading(true);
    setError(false);

    getFleetPerformanceData(params)
      .then((response) => {
        if (isMounted) {
          setData(response);
          setLoading(false);
        }
      })
      .catch(() => {
        if (isMounted) {
          setError(true);
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="fleet-performance-report_loading">
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fleet-performance-report_error">
        <p>Report could not be generated</p>
      </div>
    );
  }

  const topVehicles = data?.top_vehicles || [];
  const worstVehicles = data?.worst_vehicles || [];
  const isEmpty = topVehicles.length === 0 && worstVehicles.length === 0;

  if (isEmpty) {
    return (
      <div className="fleet-performance-report">
        <div className="fleet-performance-report_empty">
          <p>No vehicle data is available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fleet-performance-report">
      <FleetPerformanceTable
        vehicles={topVehicles}
        title="Best Of 5 Vehicle"
        sortOrder="desc"
      />
      <FleetPerformanceTable
        vehicles={worstVehicles}
        title="Worst of 5 Vehicle"
        sortOrder="asc"
      />
    </div>
  );
};

/**
 * Default export wraps the report component in an error boundary
 * so that unexpected rendering errors show a fallback UI instead
 * of crashing the entire Reports page.
 */
const FleetPerformanceReportWithBoundary = (props) => (
  <FleetPerformanceErrorBoundary>
    <FleetPerformanceReport {...props} />
  </FleetPerformanceErrorBoundary>
);

export default FleetPerformanceReportWithBoundary;
